import React from 'react';
import PropTypes from 'prop-types';
import DateRangeSelectorDialog from 'components/input/DateRange/DateRangeSelectorDialog';
import TextField from 'material-ui/TextField';
import injectSheet from 'react-jss';
import moment from 'moment-timezone';
import styles from 'components/input/DateRange/styles/DateRangePicker';
import utils from 'helpers/utils';
import { dateTimeFormat, addMonths, isAfterDate } from 'material-ui/DatePicker/dateUtils';

function fDate(date) {
    return date ? new Date(date) : date;
}

class DateRangePicker extends React.Component {
    constructor(props, context) {
        super(props, context);
        this.state = {
            rangeStart: fDate(this.props.value[0]),
            rangeEnd: fDate(this.props.value[1]),
            pickerOpen: false,
            firstMonthDay: fDate(this.props.firstMonthDay),
            lastRangeStart: fDate(this.props.value[0]),
            lastRangeEnd: fDate(this.props.value[1]),
        };

        this.handleAccept = this.handleAccept.bind(this);
        this.handleCancel = this.handleCancel.bind(this);
        this.handleReset = this.handleReset.bind(this);
        this.handleChangeRange = this.handleChangeRange.bind(this);
        this.handleFocus = this.handleFocus.bind(this);
        this.handleTouchTap = this.handleTouchTap.bind(this);
        this.handlePrevMonthClicked = this.handlePrevMonthClicked.bind(this);
        this.handleNextMonthClicked = this.handleNextMonthClicked.bind(this);
        this.handleSetRange = this.handleSetRange.bind(this);
    }

    get value() {
        return {
            start: fDate(this.state.rangeStart),
            end: fDate(this.state.rangeEnd),
        };
    }

    getRangeLabelValue(rangeStart, rangeEnd) {
        const {
            locale,
            rangeLabelDateFormat,
        } = this.props;

        let defaultRangeDateFormat = 'L';

        if (rangeStart === null) {
            return '';
        }
        if (rangeLabelDateFormat) {
            return `${ moment(rangeStart).locale(locale)
                .format(rangeLabelDateFormat) } → ${ moment(rangeEnd).locale(locale)
                .format(rangeLabelDateFormat) }`;
        }
        return `${ moment(rangeStart).locale(locale)
            .format(defaultRangeDateFormat) } → ${ moment(rangeEnd).locale(locale)
            .format(defaultRangeDateFormat) }`;

    }

    static sanityChangeRangeStartRangeEnd(rangeStart, rangeEnd) {
        if (rangeStart === null && rangeEnd !== null) {
            return {
                rangeStart: rangeEnd,
                rangeEnd: rangeEnd,
            };
        }

        return {
            rangeStart: rangeStart,
            rangeEnd: rangeEnd,
        };
    }

    componentDidUpdate(prevProps) {
        const { firstMonthDay, value } = this.props;

        if ((prevProps.value[0] !== value[0])
            || (prevProps.value[1] !== value[1])
            || (prevProps.firstMonthDay !== firstMonthDay)) {
            this.setState({
                rangeStart: fDate(value[0]),
                rangeEnd: fDate(value[1]),
                firstMonthDay: fDate(firstMonthDay),
                lastRangeStart: fDate(value[0]),
                lastRangeEnd: fDate(value[1]),
            });
        }
    }

    handleAccept() {
        const { firstMonthDay, onAccept } = this.props;
        const { rangeStart } = this.state;

        let selectedRangeEnd = this.state.rangeEnd;

        // Adding a day (minus a minute) to have the last selected day also be included in the search.
        selectedRangeEnd = moment(selectedRangeEnd).add({ h: 23, m: 59, s: 59 }).toDate();

        if (utils.isFunction(onAccept)) {
            onAccept(rangeStart, selectedRangeEnd);
        }

        const nrs = DateRangePicker.sanityChangeRangeStartRangeEnd(rangeStart, selectedRangeEnd).rangeStart;
        const nre = DateRangePicker.sanityChangeRangeStartRangeEnd(rangeStart, selectedRangeEnd).rangeEnd;

        this.setState({
            pickerOpen: false,
            firstMonthDay: nrs === null ? fDate(firstMonthDay) : nrs,
            lastRangeStart: nrs,
            lastRangeEnd: nre,
        });
    }

    handleCancel() {
        const { firstMonthDay, onCancel } = this.props;

        if (utils.isFunction(onCancel)) {
            onCancel();
        }

        let nrs = DateRangePicker.sanityChangeRangeStartRangeEnd(this.state.lastRangeStart, this.state.lastRangeEnd).rangeStart;
        let nre = DateRangePicker.sanityChangeRangeStartRangeEnd(this.state.lastRangeStart, this.state.lastRangeEnd).rangeEnd;

        this.setState({
            pickerOpen: false,
            firstMonthDay: nrs === null ? fDate(firstMonthDay) : nrs,
            rangeStart: nrs,
            rangeEnd: nre,
        });
    }

    handleReset() {
        const { onReset, value } = this.props;

        if (utils.isFunction(onReset)) {
            onReset();
        }

        let nrs = DateRangePicker.sanityChangeRangeStartRangeEnd(fDate(value[0]), value[1]).rangeStart;
        let nre = DateRangePicker.sanityChangeRangeStartRangeEnd(fDate(value[0]), value[1]).rangeEnd;

        this.setState({
            rangeStart: nrs,
            rangeEnd: nre,
        });
    }

    handleChangeRange(date) {
        const { onChangeDateRange } = this.props;

        let nre = date,
            nrs = date;

        if (this.state.rangeStart !== null && (this.state.rangeEnd === null || (this.state.rangeStart === this.state.rangeEnd))) {
            nrs = this.state.rangeStart;
        }

        if (isAfterDate(nrs, nre)) {
            nrs = nre;
        }

        nrs = DateRangePicker.sanityChangeRangeStartRangeEnd(nrs, nre).rangeStart;
        nre = DateRangePicker.sanityChangeRangeStartRangeEnd(nrs, nre).rangeEnd;

        this.setState({
            rangeStart: nrs,
            rangeEnd: nre,
        }, () => {
            if (utils.isFunction(onChangeDateRange)) {
                onChangeDateRange(nrs, nre);
            }
        });
    }

    handleSetRange(rangeStart, rangeEnd) {
        const { autoClose, onAccept, onChangeDateRange } = this.props;

        let newRangeEnd = rangeEnd,
            newRangeStart = rangeStart;

        newRangeStart = DateRangePicker.sanityChangeRangeStartRangeEnd(newRangeStart, newRangeEnd).rangeStart;
        newRangeEnd = DateRangePicker.sanityChangeRangeStartRangeEnd(newRangeStart, newRangeEnd).rangeEnd;

        let setStateObject = {
            rangeStart: newRangeStart,
            rangeEnd: newRangeEnd,
        };

        if (autoClose === true) {
            setStateObject.pickerOpen = false;
            setStateObject.firstMonthDay = newRangeStart === null ? fDate(this.props.firstMonthDay) : newRangeStart;

            if (utils.isFunction(onAccept)) {
                onAccept(newRangeStart, newRangeEnd);
            }
        } else {
            if (utils.isFunction(onChangeDateRange)) {
                onChangeDateRange(newRangeStart, newRangeEnd);
            }
        }

        this.setState(setStateObject);
    }

    handleFocus(event) {
        event.target.blur();

        const { onFocus } = this.props;

        if (utils.isFunction(onFocus)) {
            onFocus(event);
        }
    }

    handleTouchTap(event) {
        const { disabled, onOpen } = this.props;

        if (!disabled) {
            this.setState({
                pickerOpen: true,
            });
        }

        if (utils.isFunction(onOpen)) {
            onOpen(event);
        }
    }

    handlePrevMonthClicked() {
        const { onMonthChange } = this.props;
        const { firstMonthDay } = this.state;

        if (utils.isFunction(onMonthChange)) {
            onMonthChange(addMonths(firstMonthDay, 1));
        }

        this.setState((prevState) => {
            return {
                firstMonthDay: addMonths(prevState.firstMonthDay, 1),
            };
        });
    }

    handleNextMonthClicked() {
        const { onMonthChange } = this.props;
        const { firstMonthDay } = this.state;

        if (utils.isFunction(onMonthChange)) {
            onMonthChange(addMonths(firstMonthDay, -1));
        }

        this.setState((prevState) => {
            return {
                firstMonthDay: addMonths(prevState.firstMonthDay, -1),
            };
        });
    }

    formatRangeDates(ranges) {
        if (ranges && ranges.length > 0) {
            ranges.forEach((v) => {
                v.range.forEach((v1, i) => {
                    v.range[i] = new Date(v1);
                });
            });
            return ranges;
        }
    }

    render() {
        const {
            animation,
            cancelLabel,
            customRanges,
            dialogStyles,
            errorText,
            firstDayOfWeek,
            floatingLabelFixed,
            floatingLabelText,
            fullWidth,
            hintText,
            labelText,
            locale,
            maxDate,
            minDate,
            noOfCalendars,
            okLabel,
            resetLabel,
            shouldDisableDate,
        } = this.props;

        const {
            firstMonthDay,
            rangeEnd,
            rangeStart,
            pickerOpen,
        } = this.state;

        return (
            <div>
                <TextField
                    errorText={ errorText }
                    floatingLabelFixed={ floatingLabelFixed }
                    floatingLabelText={ floatingLabelText }
                    fullWidth={ fullWidth }
                    hintText={ hintText }
                    name={ labelText }
                    onFocus={ this.handleFocus }
                    onTouchTap={ this.handleTouchTap }
                    value={ this.getRangeLabelValue(rangeStart, rangeEnd) }
                />

                <DateRangeSelectorDialog
                    DateTimeFormat={ dateTimeFormat }
                    animation={ animation }
                    cancelLabel={ cancelLabel }
                    customRanges={ this.formatRangeDates(customRanges) }
                    firstDayOfWeek={ firstDayOfWeek }
                    firstMonthDay={ firstMonthDay }
                    locale={ locale }
                    maxDate={ fDate(maxDate) }
                    minDate={ fDate(minDate) }
                    noOfCalendars={ noOfCalendars }
                    okLabel={ okLabel }
                    onAccept={ this.handleAccept }
                    onCancel={ this.handleCancel }
                    onChange={ this.handleChangeRange }
                    onNextMonthButtonClicked={ this.handleNextMonthClicked }
                    onPrevMonthButtonClicked={ this.handlePrevMonthClicked }
                    onReset={ this.handleReset }
                    rangeEnd={ rangeEnd }
                    rangeStart={ rangeStart }
                    resetLabel={ resetLabel }
                    setRange={ this.handleSetRange }
                    shouldDisableDate={ shouldDisableDate }
                    showDialog={ pickerOpen }
                    style={ dialogStyles }
                />
            </div>
        );
    }
}

DateRangePicker.defaultProps = {
    autoClose: false,
    cancelLabel: 'Cancel',
    errorText: null,
    firstDayOfWeek: 1,
    firstMonthDay: new Date(),
    labelText: 'Select Date Range',
    floatingLabelStyle: {
        fontWeight: '100',
    },
    locale: 'en-US',
    noOfCalendars: 2,
    okLabel: 'Ok',
    value: [
        null,
        null,
    ],
};

DateRangePicker.propTypes = {
    animation: PropTypes.func,
    autoClose: PropTypes.bool,
    cancelLabel: PropTypes.string,
    customRanges: PropTypes.arrayOf(PropTypes.shape({
        label: PropTypes.string,
        range: PropTypes.array,
    })),
    dialogStyles: PropTypes.object,
    disabled: PropTypes.bool,
    errorText: PropTypes.string,
    firstDayOfWeek: PropTypes.number,
    firstMonthDay: PropTypes.any,
    floatingLabelFixed: PropTypes.bool,
    floatingLabelText: PropTypes.string,
    fullWidth: PropTypes.bool,
    hintText: PropTypes.string,
    labelText: PropTypes.string,
    locale: PropTypes.string,
    maxDate: PropTypes.any,
    minDate: PropTypes.any,
    noOfCalendars: PropTypes.number,
    okLabel: PropTypes.string,
    onAccept: PropTypes.func,
    onCancel: PropTypes.func,
    onChangeDateRange: PropTypes.func,
    onFocus: PropTypes.func,
    onMonthChange: PropTypes.func,
    onOpen: PropTypes.func,
    onReset: PropTypes.func,
    rangeLabelDateFormat: PropTypes.string,
    resetLabel: PropTypes.string,
    shouldDisableDate: PropTypes.func,
    value: PropTypes.array,
};

export default injectSheet(styles)(DateRangePicker);



// WEBPACK FOOTER //
// ./src/components/input/DateRange/DateRangePicker.js